import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import Button, { Color } from "../../../../../components/Form/Button";
import { FormFeedbackCard } from "../../../../../components/Form/FeedbackCard";
import Input from "../../../../../components/Form/Input";
import { PASSWORD_MAX_STRENGTH } from "../../../../../constants";
import useYupValidation from "../../../../../hooks/useYupValidation";
import {
  MAX_CHARACTERS_PASSWORD,
  MIN_CHARACTERS_PASSWORD,
  RGX_SPECIAL_CHARACTERS,
} from "../../utils";
import PasswordStrengthBar from "./PasswordStrengthBar";
import PasswordValidate from "./PasswordValidate";
import { ShowPasswordIcon } from "./ShowPasswordIcon";
import { BoxContainer, MainGrid, Text, Title } from "./styles";

interface UpdatePasswordFormProps {
  handleSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
  formError?: string;
  clearFormErrors?: () => void;
  passwordComplexity?: boolean;
}

export interface UpdatePasswordFormHandle {
  password: string;
}

const UpdatePasswordForm: ForwardRefRenderFunction<
  UpdatePasswordFormHandle,
  UpdatePasswordFormProps
> = (props, ref) => {
  const { handleSubmit, isLoading, formError, clearFormErrors, passwordComplexity } = props;
  const { t: translate } = useTranslation();
  const [passwordValidate, setPasswordValidate] = useState(false);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const hasFormError = useMemo(() => !!formError?.length, [formError]);
  const clearErrors = () => {
    setErrors([]);
  };
  let baseValidate = {
    password: yup
      .string()
      .typeError(translate("pleaseEnterAPassword"))
      .required(translate("pleaseEnterAPassword")),
    confirmPassword: yup
      .string()
      .typeError(translate("passwordAndConfirmPasswordMustBeTheSame"))
      .oneOf([yup.ref("password"), null], translate("passwordAndConfirmPasswordMustBeTheSame")),
  };

  const validateForm = () => {
    try {
      const schema = yup.object().shape(baseValidate);

      schema.validateSync(
        {
          password,
          confirmPassword,
        },
        { abortEarly: false }
      );

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
      }

      return false;
    }
  };

  useEffect(() => {
    if (passwordComplexity) {
      baseValidate?.password
        .test(
          "min-len",
          translate("yourPasswordMustBeAtLeast12CharactersLong"),
          (password) => !!password && password.length >= MIN_CHARACTERS_PASSWORD
        )
        .test(
          "max-len",
          translate("yourPasswordMustBeAtMaximumOf30CharactersLong"),
          (password) => !!password && password.length < MAX_CHARACTERS_PASSWORD
        )
        .test(
          "min-special-len",
          translate("yourPasswordMustContainAtLeastOneSpecialCharacter"),
          (password) => !!password && !!password.match(RGX_SPECIAL_CHARACTERS)
        );
    }
  }, [passwordComplexity]);

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[#@$&]/.test(password);
  const isLongEnough = password.length >= PASSWORD_MAX_STRENGTH;

  const isPasswordStrong = () => {
    if (hasUppercase && hasLowercase && hasNumber && hasSpecialChar && isLongEnough) {
      return true;
    }

    if (password?.length) {
      setTimeout(() => {
        setPasswordValidate(passwordComplexity ?? true);
      }, 200);
    }

    return false;
  };

  const handleSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const conditionSubmitForm = passwordComplexity
      ? passwordComplexity && isPasswordStrong() && validateForm()
      : validateForm();

    if (conditionSubmitForm) {
      handleSubmit?.(event);
    }
  };

  useImperativeHandle(ref, () => ({
    password,
  }));

  const handleFocus = () => {
    setPasswordValidate(passwordComplexity ?? true);
    clearErrors();
    clearFormErrors?.();
  };

  const handleBlur = () => {
    setPasswordValidate(false);
  };

  const removeConfirmPasswordError = () =>
    setErrors((prev) => prev.filter((error) => error.path !== "confirmPassword"));

  return (
    <BoxContainer>
      <MainGrid justifyContent="center" alignItems="flex-start" spacing={3}>
        <Title>{translate("newPassword")}</Title>
        <Text>{translate("pleaseCreateANewPasswordForYourAccount")}</Text>
        <FormFeedbackCard text={formError} hide={!hasFormError} />
        <form className="w-100" onSubmit={handleSubmitForm}>
          <Stack width="100%" justifyContent="center" alignItems="flex-start" spacing={4}>
            <Input
              required
              fullWidth
              type={showPassword ? "text" : "password"}
              label={translate("newPassword")}
              value={password}
              name="user-pwd"
              id="pwd"
              data-cy="updatePasswordField"
              disabled={isLoading}
              onChange={(event) => {
                const nextPassword = event.target.value;
                setPassword(nextPassword);
                if (nextPassword === confirmPassword) removeConfirmPasswordError();
              }}
              error={fieldHasError("password") || hasFormError}
              errorMessage={getErrorByField("password")}
              onFocus={handleFocus}
              onBlur={handleBlur}
              rightIcon={
                <ShowPasswordIcon showPassword={showPassword} setShowPassword={setShowPassword} />
              }
              onClick={clearFormErrors}
              inputProps={{
                autoComplete: "new-password",
              }}
            />
            {passwordValidate && password && <PasswordStrengthBar password={password} />}
            <div style={{ marginTop: password ? "6px" : "12px" }}>
              <PasswordValidate
                hasUppercase={hasUppercase}
                hasLowercase={hasLowercase}
                hasNumber={hasNumber}
                hasSpecialChar={hasSpecialChar}
                isLongEnough={isLongEnough}
              />
            </div>
            <Input
              required
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              label={translate("confirmPassword")}
              value={confirmPassword}
              name="user-rep-pwd"
              id="rep-pwd"
              data-cy="updatePasswordConfirmField"
              disabled={isLoading}
              onChange={(event) => {
                const nextConfirmPassword = event.target.value;
                setConfirmPassword(nextConfirmPassword);
                if (nextConfirmPassword === password) removeConfirmPasswordError();
              }}
              error={fieldHasError("confirmPassword") || hasFormError}
              errorMessage={getErrorByField("confirmPassword")}
              rightIcon={
                <ShowPasswordIcon
                  showPassword={showConfirmPassword}
                  setShowPassword={setShowConfirmPassword}
                />
              }
              onClick={clearFormErrors}
              inputProps={{
                autoComplete: "new-password",
              }}
            />
            <Button
              fullWidth
              type={"submit"}
              customColor={Color.SECONDARY}
              loading={isLoading}
              data-cy="updatePasswordButton"
              aria-label="submitButton"
            >
              {translate("createNewPassword")}
            </Button>
          </Stack>
        </form>
      </MainGrid>
    </BoxContainer>
  );
};

export default forwardRef(UpdatePasswordForm);
